import * as React from 'react';
import getConfig from 'next/config';
import { Typography } from '@mui/material';
import { HTMLElement, parse, TextNode } from 'node-html-parser';
import { Link, LinkProps } from '@procore/core-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CTA_LINK } from '../../constants';
import { useStyles } from './I18nDOMTransformer.styles';
import AnalyticsWrappedComponent from '../Analytics/AnalyticsWrappedComponent';

const { publicRuntimeConfig } = getConfig();

export const I18nDOMTransformer = (props: {
  onClick?: LinkProps['onClick'];
  target?: string;
  text: string;
  analyticsLabel: string;
}): any => {
  const classes = useStyles();
  const { intakeForm } = useFlags();

  const parseResults = React.useMemo(() => {
    const tempDoc = parse(props.text);
    const nodes = tempDoc.childNodes;
    return nodes.reduce((acc: any[], node) => {
      const { innerText } = node;
      if (node instanceof TextNode) {
        acc.push(
          <span key={node.textContent?.slice(0, 20)} suppressHydrationWarning>
            {node.textContent}
          </span>
        );
      } else if (node instanceof HTMLElement && node.tagName === 'A') {
        const { id } = node;
        if (id === 'system_status') {
          acc.push(
            <AnalyticsWrappedComponent
              key={id}
              componentType={Link}
              eventText="Clicked System Status"
              eventLabel={props.analyticsLabel}
              data-track-click="Maintenance Page, Navigation, system status"
              href="https://status.procore.com"
            >
              <span suppressHydrationWarning>{innerText}</span>
            </AnalyticsWrappedComponent>
          );
        } else if (id === 'join_the_network') {
          acc.push(
            <AnalyticsWrappedComponent
              key={id}
              componentType={Link}
              eventText="Clicked Join the Network Button"
              eventLabel={props.analyticsLabel}
              data-track-click="FAQ, Join The Network"
              href={
                intakeForm ? `${publicRuntimeConfig.basePath}/signup` : CTA_LINK
              }
              target={intakeForm ? undefined : '_blank'}
              rel={intakeForm ? undefined : 'noreferrer'}
              className={classes.link}
            >
              <Typography component="span" suppressHydrationWarning>
                {innerText}
              </Typography>
            </AnalyticsWrappedComponent>
          );
        } else if (id === 'request_a_demo') {
          acc.push(
            <AnalyticsWrappedComponent
              key={id}
              componentType={Link}
              eventText="Clicked Request a Demo"
              eventLabel={props.analyticsLabel}
              data-track-click="FAQ, Request a Demo"
              href="https://www.procore.com/offer/demo/values"
              rel="nofollow"
              className={classes.link}
            >
              <Typography component="span" suppressHydrationWarning>
                {innerText}
              </Typography>
            </AnalyticsWrappedComponent>
          );
        }
      }
      return acc;
    }, []);
  }, [
    props.text,
    props.onClick,
    props.target,
    intakeForm,
    props.analyticsLabel,
  ]);

  return parseResults;
};
